import { css } from '@emotion/react';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useConfirmationModal } from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { HOME_ROUTE } from '~/_shared/constants/routes';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  type ApiError, getApiErrorErrors,
} from '~/_shared/utils/api/apiError.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { ImpersonationService } from '~/authentication/impersonation';
import { type ImpersonateResponse } from '~/impersonation/impersonation.repository';
import {
  clientInviteeListChanged,
  clientMemberListChanged,
} from '~/store/frontendState/client/client.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { userGetDataRequest } from '~/store/userData/userData.actionCreators';
import {
  useUserCurrentClientSelector, useUserDataSelector,
} from '~/store/userData/userData.selectors';
import { useReloadUserData } from '~/store/userData/useReloadUserData.hook';
import { Trans } from '~/translations/Trans';
import { type MemberRole } from '../memberRole.type';
import {
  MemberStatus, type TeamMemberLimitsData,
} from '../teamManagement.repository';
import { type InvitationAndMemberData } from '../teamManagementModal.container';
import { useGetRoles } from '../useTeamManagement';
import { TeamManagementMenuComponent } from './teamManagementMenu.component';
import { useMembersErrorModal } from './useMembersErrorModal';

const textBlockStyles = css({
  margin: 0,
  padding: '3px 0',
});

const listIndentStyle = css({
  paddingLeft: 24,
  paddingBottom: 15,
});

const paddingStyle = css({
  padding: '15px 0',
});

const cancelEmphasisStyles = (theme: Theme) => css({
  color: theme.buttonColors.secondaryBackground,
});

type TeamManagementMenuContainerProps = {
  member: InvitationAndMemberData;
  limits: TeamMemberLimitsData;

  close: () => void;
  requestDeleteUser: (clientId: number, memberId: number) => Promise<void>;
  enableMember: (clientId: number, memberId: number, enable: boolean) => Promise<void>;
  changeAllowPublicExport: (clientId: number, memberId: number, enable: boolean) => Promise<void>;
  changeRole: (clientId: number, memberId: number, roleId: number) => Promise<void>;
  revokeInvite: (clientId: number, invitationId: string) => Promise<void>;
  loginAsMember: (clientId: number, memberId: number) => Promise<ImpersonateResponse>;
  sendPasswordResetEmail: (email: string, callback: () => void) => void;
};

export const TeamManagementMenuContainer: FC<TeamManagementMenuContainerProps> = ({
  member,
  close,
  requestDeleteUser,
  enableMember,
  changeAllowPublicExport,
  changeRole,
  revokeInvite,
  loginAsMember,
  sendPasswordResetEmail,
  limits,
}) => {
  const clientId = useClientIdSelector();
  const isClientMigrationInProgress = !useUserCurrentClientSelector()?.migrationDone;
  const { clientRole: currentMemberRole, id: currentMemberId } = useUserDataSelector();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { openErrorModal } = useMembersErrorModal();
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();
  const { data: roles, invokeAjax: getRoles } = useGetRoles();
  const { reloadUserData } = useReloadUserData();

  const [passwordResetEmailSent, setPasswordResetEmailSent] = useState<boolean>(false);
  const [currentError, setCurrentError] = useState<string>('');

  const currentMemberData = useMemo(() => ({
    id: currentMemberId,
    role: currentMemberRole,
  }), [currentMemberId, currentMemberRole]);

  const onLoginAsUser = useCallback(() => {
    if (!clientId || !member.id) {
      return;
    }

    loginAsMember(clientId, member.id)
      .then((response) => {
        ImpersonationService.start(response.access_token_expiration);
        close();
        navigate(HOME_ROUTE);
        location.reload();
      }).catch((e: ApiError) => {
        setCurrentError(getApiErrorErrors(e)?.join(', ') || e.message);
      });
  }, [clientId, close, navigate, loginAsMember, member.id]);

  const onSentPasswordRecoveryEmail = useCallback(() => {
    if (!member.email) {
      return;
    }
    sendPasswordResetEmail(member.email, () => {
      setPasswordResetEmailSent(true);
    });
  }, [member.email, sendPasswordResetEmail]);

  const onEnableOrDisableUser = useCallback(() => {
    if (!clientId || !member.id) {
      return;
    }

    enableMember(clientId, member.id, member.status !== MemberStatus.ENABLED)
      .then(() => {
        dispatch(clientMemberListChanged());
        close();
      }).catch((e: ApiError) => {
        setCurrentError(getApiErrorErrors(e)?.join(', ') || e.message);
      });
  }, [clientId, close, dispatch, enableMember, member.id, member.status]);

  const deleteUser = useCallback(() => {
    if (!clientId || !member.id) {
      return;
    }

    requestDeleteUser(clientId, member.id)
      .then(() => {
        dispatch(clientMemberListChanged());
        close();
      }).catch((e: ApiError) => {
        setCurrentError(getApiErrorErrors(e)?.join(', ') || e.message);
      });
  }, [clientId, close, dispatch, member.id, requestDeleteUser]);

  const openConfirmUserDataTransferModal = useCallback(() => {
    openConfirmationModal({
      confirmCaption: t('Proceed'),
      isConfirmButtonDestructive: true,
      onCancel: closeConfirmationModal,
      onConfirm: () => {
        deleteUser();
        closeConfirmationModal();
      },
      text: (
        <div>
          <p css={[textBlockStyles, paddingStyle]}>
            {t('userManagement.deleteUser.mapWillBeTransferredWarning.description.explanation')}
          </p>
          <p css={[textBlockStyles, paddingStyle]}>
            <Trans
              i18nKey="userManagement.deleteUser.mapWillBeTransferredWarning.description.prevent"
              components={{
                CancelEmphasis: <span css={cancelEmphasisStyles} />,
              }}
            />
          </p>
          <ol css={[textBlockStyles, listIndentStyle]}>
            <li css={textBlockStyles}>
              {t('userManagement.deleteUser.mapWillBeTransferredWarning.description.prevent.option1')}
            </li>
            <li css={textBlockStyles}>
              {t('userManagement.deleteUser.mapWillBeTransferredWarning.description.prevent.option2', {
                functionalityName: t('Login as User'),
                functionalityContainerName: t('Manage Team Users'),
              })}
            </li>
            <li css={textBlockStyles}>
              {t('userManagement.deleteUser.mapWillBeTransferredWarning.description.prevent.option3', {
                functionalityName: t('Disable User'),
              })}
            </li>
          </ol>
        </div>
      ),
      title: t('userManagement.deleteUser.mapWillBeTransferredWarning.title'),
    });
  }, [closeConfirmationModal, deleteUser, openConfirmationModal, t]);

  const confirmDeleteUser = useCallback(() => {
    openConfirmationModal({
      confirmCaption: t('Proceed'),
      isConfirmButtonDestructive: true,
      onCancel: closeConfirmationModal,
      onConfirm: () => {
        closeConfirmationModal();
        openConfirmUserDataTransferModal();
      },
      text: t('You are about to delete this member. Once deleted, a member cannot be re-added. Do you wish to proceed?'),
      title: t('Delete User'),
    });
  }, [closeConfirmationModal, openConfirmUserDataTransferModal, openConfirmationModal, t]);

  const onChangeRole = useCallback((role: MemberRole) => {
    if (!clientId || !member.id || !roles?.data) {
      return;
    }

    const roleId = roles.data.find((item) => item.name === role)?.id || null;

    if (roleId === null) {
      return;
    }

    changeRole(clientId, member.id, roleId)
      .then(() => {
        dispatch(clientMemberListChanged());
        dispatch(dispatch(userGetDataRequest()));
        close();
      }).catch((e: ApiError) => {
        setCurrentError(getApiErrorErrors(e)?.join(', ') || e.message);
      });
  }, [changeRole, clientId, close, dispatch, member.id, roles]);

  const onRevokeInvite = useCallback(() => {
    if (!clientId || !member.invitationId) {
      return;
    }

    revokeInvite(clientId, member.invitationId)
      .then(() => {
        dispatch(clientInviteeListChanged());
        dispatch(clientMemberListChanged());
        close();
      }).catch((e: ApiError) => {
        setCurrentError(getApiErrorErrors(e)?.join(', ') || e.message);
      });
  }, [clientId, close, dispatch, member.invitationId, revokeInvite]);

  const handleChangeAllowPublicExport = useCallback((newValue: boolean) => {
    if (!clientId || !member.id) {
      return;
    }

    changeAllowPublicExport(clientId, member.id, newValue)
      .then(() => {
        dispatch(clientMemberListChanged());
        if (member.id === currentMemberId) {
          reloadUserData();
        }
        close();
      }).catch((e: ApiError) => {
        setCurrentError(getApiErrorErrors(e)?.join(', ') || e.message);
      });
  }, [changeAllowPublicExport, clientId, close, currentMemberId, dispatch, member.id, reloadUserData]);

  useEffect(() => {
    getRoles();
  }, [getRoles]);

  useEffect(() => {
    if (currentError) {
      close();
      openErrorModal(currentError);
    }
  }, [close, currentError, openErrorModal, t]);

  return (
    <TeamManagementMenuComponent
      allowPublicExport={() => handleChangeAllowPublicExport(true)}
      disallowPublicExport={() => handleChangeAllowPublicExport(false)}
      currentMemberData={currentMemberData}
      isClientMigrationInProgress={isClientMigrationInProgress}
      isLoading={!roles}
      limits={limits}
      member={member}
      passwordResetEmailSent={passwordResetEmailSent}
      onChangeRole={onChangeRole}
      onDeleteUser={confirmDeleteUser}
      onEnableOrDisableUser={onEnableOrDisableUser}
      onLoginAsUser={onLoginAsUser}
      onRevokeInvite={onRevokeInvite}
      onSentPasswordRecoveryEmail={onSentPasswordRecoveryEmail}
    />
  );
};
